import ajax from '@ep/tools/ajax';
import toast from '@ep/tools/toast';
import create from '@ep/components/create/element';

const d = (path, fallback = '') => {
  const parts = String(path || '').split('.').filter(Boolean);
  let cursor = globalThis?.dic;
  for (const part of parts) {
    if (!cursor || typeof cursor !== 'object') return fallback;
    cursor = cursor[part];
  }
  return typeof cursor === 'string' ? cursor : fallback;
};

const post = (method, body = {}) => ajax.post({
  product: 'admindigital',
  category: 'communication',
  domain: 'channels',
  module: 'channelAccounts',
  method,
  body,
  config: { loader: true },
});

const refresh = async (component) => {
  if (component && typeof component.render === 'function') {
    await component.render();
  }
};

const errorMessage = (response = {}) => (
  d(`channelAccounts.errors.${response?.code || response?.error || ''}`)
  || d('channelAccounts.messages.operationFailed')
);

const runMutation = async ({
  method,
  body,
  successMessage,
  component,
} = {}) => {
  try {
    const response = await post(method, body);
    if (response?.status !== 'success') {
      throw Object.assign(
        new Error(errorMessage(response)),
        { response }
      );
    }

    toast({
      type: 'success',
      message: d(successMessage),
    });
    await refresh(component);
    return response;
  } catch (error) {
    toast({
      type: 'error',
      message: error?.message || errorMessage(error?.response),
    });
    return null;
  }
};

const openCredentialDialog = async ({ record = {}, component = null } = {}) => {
  let username = '';
  let password = '';

  return create({
    element: 'div',
    parent: document.body,
    class: 'channel-account-credential-panel',
    dialog: {
      modal: true,
      autoOpen: true,
      kill: true,
      effect: 'zoom',
      title: d('channelAccounts.dialogs.credentialTitle'),
      buttons: [
        {
          text: d('labels.cancel'),
          icon: { type: 'solid', name: 'ban' },
          close: true,
        },
        {
          text: d('labels.save'),
          icon: { type: 'solid', name: 'floppy-disk' },
          close: false,
          click: async (event) => {
            const button = event.currentTarget;
            if (!username || !password) return;

            button.disabled = true;
            try {
              const result = await runMutation({
                method: 'setCredential',
                body: {
                  id: record.id,
                  credential: {
                    username,
                    password,
                  },
                },
                successMessage: 'channelAccounts.messages.credentialSaved',
                component,
              });

              if (result) {
                event.currentTarget
                  .closest('.dialog')
                  ?.instance
                  ?.close?.('destroy');
              }
            } finally {
              button.disabled = false;
            }
          },
        },
      ],
    },
    isGrid: true,
    cols: 2,
    content: [
      {
        tag: 'p',
        text: d('channelAccounts.dialogs.credentialDescription'),
        row: 0,
        col: 0,
        colspan: 2,
      },
      {
        element: 'text',
        id: 'channel-account-credential-username',
        label: d('channelAccounts.fields.username'),
        required: true,
        full: true,
        row: 1,
        col: 0,
        colspan: 2,
        input: (event) => {
          username = String(event?.currentTarget?.value || '');
        },
      },
      {
        element: 'password',
        id: 'channel-account-credential-password',
        label: d('channelAccounts.fields.password'),
        required: true,
        full: true,
        row: 2,
        col: 0,
        colspan: 2,
        input: (event) => {
          password = String(event?.currentTarget?.value || '');
        },
      },
    ],
  });
};

export const channelAccountActionRegistry = Object.freeze({
  'channelAccounts.credential': {
    visible: ({ record = {} } = {}) => (
      Boolean(record.id)
      && record.channelCode === 'email'
      && !record.providerCode
    ),
    enabled: ({ record = {} } = {}) => (
      Boolean(record.id)
      && record.channelCode === 'email'
      && !record.providerCode
    ),
    run: openCredentialDialog,
  },
  'channelAccounts.validate': {
    visible: ({ record = {} } = {}) => Boolean(record.id),
    enabled: ({ record = {} } = {}) => Boolean(record.id),
    confirm: {
      title: d('channelAccounts.confirmations.validateTitle'),
      message: d('channelAccounts.confirmations.validate'),
    },
    run: ({ record, component }) => runMutation({
      method: 'validateConnection',
      body: { id: record.id },
      successMessage: 'channelAccounts.messages.validated',
      component,
    }),
  },
  'channelAccounts.activate': {
    visible: ({ record = {} } = {}) => record.isActive !== true,
    enabled: ({ record = {} } = {}) => (
      record.isActive !== true
      && String(record.validationCode || '').toLowerCase() === 'valid'
    ),
    confirm: {
      title: d('channelAccounts.confirmations.activateTitle'),
      message: d('channelAccounts.confirmations.activate'),
    },
    run: ({ record, component }) => runMutation({
      method: 'setActive',
      body: {
        id: record.id,
        isActive: true,
      },
      successMessage: 'channelAccounts.messages.activated',
      component,
    }),
  },
  'channelAccounts.deactivate': {
    visible: ({ record = {} } = {}) => record.isActive === true,
    enabled: ({ record = {} } = {}) => record.isActive === true,
    confirm: {
      title: d('channelAccounts.confirmations.deactivateTitle'),
      message: d('channelAccounts.confirmations.deactivate'),
    },
    run: ({ record, component }) => runMutation({
      method: 'setActive',
      body: {
        id: record.id,
        isActive: false,
      },
      successMessage: 'channelAccounts.messages.deactivated',
      component,
    }),
  },
});

export default channelAccountActionRegistry;
